import React, { useState, useEffect } from 'react';
import { notificationService } from '../services/notificationService';

const NotificationSettings = ({ donor, onUpdate }) => {
  const [preferences, setPreferences] = useState({
    bloodRequests: true,
    urgentRequests: true,
    compatibleBloodGroups: true,
    locationRadius: 50,
    enabledMethods: ['push'],
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '07:00'
    }
  });
  
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (donor?.notificationPreferences) {
      setPreferences(donor.notificationPreferences);
    }
  }, [donor]);

  const handleEnableNotifications = async () => {
    setLoading(true);
    try {
      await notificationService.requestPermission();
      await notificationService.registerDonor(donor.id, {
        notificationRadius: preferences.locationRadius
      });
      
      setNotificationPermission('granted');
      alert('Notifications enabled successfully!');
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      alert('Failed to enable notifications. Please check your browser settings.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleQuietHoursChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      quietHours: {
        ...prev.quietHours,
        [key]: value
      }
    }));
  };

  const handleMethodToggle = (method) => {
    setPreferences(prev => ({
      ...prev,
      enabledMethods: prev.enabledMethods.includes(method)
        ? prev.enabledMethods.filter(m => m !== method)
        : [...prev.enabledMethods, method]
    }));
  };

  const handleSavePreferences = async () => {
    setLoading(true);
    try {
      await notificationService.updateNotificationPreferences(donor.id, preferences);
      if (onUpdate) {
        onUpdate({ ...donor, notificationPreferences: preferences });
      }
      alert('Notification preferences saved successfully!');
    } catch (error) {
      console.error('Failed to save preferences:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">🔔 Notification Settings</h3>
      
      {notificationPermission !== 'granted' ? (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-center">
            <div className="text-yellow-600 mr-3">⚠️</div>
            <div>
              <h4 className="text-yellow-800 font-medium">Enable Notifications</h4>
              <p className="text-yellow-700 text-sm mt-1">
                Get instant alerts when blood requests match your location and blood group.
              </p>
              <button
                onClick={handleEnableNotifications}
                disabled={loading}
                className="mt-3 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
              >
                {loading ? 'Enabling...' : 'Enable Notifications'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center text-green-700">
            <span className="mr-2">✅</span>
            <span className="text-sm">Notifications are enabled</span>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Notification Types */}
        <div>
          <h4 className="text-md font-medium text-gray-800 mb-3">Notification Types</h4>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.bloodRequests}
                onChange={(e) => handlePreferenceChange('bloodRequests', e.target.checked)}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="ml-2 text-sm text-gray-700">All blood requests in my area</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.urgentRequests}
                onChange={(e) => handlePreferenceChange('urgentRequests', e.target.checked)}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="ml-2 text-sm text-gray-700">Only urgent/critical requests</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.compatibleBloodGroups}
                onChange={(e) => handlePreferenceChange('compatibleBloodGroups', e.target.checked)}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="ml-2 text-sm text-gray-700">Only compatible blood groups</span>
            </label>
          </div>
        </div>

        {/* Location Radius */}
        <div>
          <h4 className="text-md font-medium text-gray-800 mb-3">Location Range</h4>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="5"
              max="100"
              step="5"
              value={preferences.locationRadius}
              onChange={(e) => handlePreferenceChange('locationRadius', parseInt(e.target.value))}
              className="flex-1"
            />
            <span className="text-sm text-gray-600 min-w-0">
              {preferences.locationRadius} km
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Receive notifications for requests within this distance
          </p>
        </div>

        {/* Notification Methods */}
        <div>
          <h4 className="text-md font-medium text-gray-800 mb-3">Notification Methods</h4>
          <div className="space-y-2">
            {[
              { key: 'push', label: '📱 Push Notifications', description: 'Browser/app notifications' },
              { key: 'sms', label: '💬 SMS', description: 'Text messages to your phone' },
              { key: 'email', label: '📧 Email', description: 'Email notifications' }
            ].map(method => (
              <label key={method.key} className="flex items-start">
                <input
                  type="checkbox"
                  checked={preferences.enabledMethods.includes(method.key)}
                  onChange={() => handleMethodToggle(method.key)}
                  className="mt-1 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <div className="ml-2">
                  <div className="text-sm text-gray-700">{method.label}</div>
                  <div className="text-xs text-gray-500">{method.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Quiet Hours */}
        <div>
          <h4 className="text-md font-medium text-gray-800 mb-3">Quiet Hours</h4>
          <label className="flex items-center mb-3">
            <input
              type="checkbox"
              checked={preferences.quietHours.enabled}
              onChange={(e) => handleQuietHoursChange('enabled', e.target.checked)}
              className="rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="ml-2 text-sm text-gray-700">Enable quiet hours</span>
          </label>
          
          {preferences.quietHours.enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <input
                  type="time"
                  value={preferences.quietHours.start}
                  onChange={(e) => handleQuietHoursChange('start', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <input
                  type="time"
                  value={preferences.quietHours.end}
                  onChange={(e) => handleQuietHoursChange('end', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">
            No notifications will be sent during quiet hours (except critical emergencies)
          </p>
        </div>

        {/* Save Button */}
        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={handleSavePreferences}
            disabled={loading}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Notification Preferences'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
